import React, { useEffect, useState } from "react";
import { Autocomplete, Checkbox, Grid, TextField } from "@mui/material";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import InfoIcon from "@mui/icons-material/Info";
import { BASE_URL, DISABLED_MODULE } from "../../../global";
import axiosWithToken from "../../../utils/commonComponent/axiosTokenConfig";
import { CancelButton, SaveAndUpdate, UploadButton } from "../../../utils/Custom/Button";
import { moduleList, userRoles, userTypes, } from "../../constants/userConstant";
import { AutoCompletes, TextFields } from "../../../utils/Custom/custominput";
import Text from "../../../utils/Custom/text";
import { fetchQuery } from "../../../utils/commonComponent/fetched";
import { fetchRBACData, getPermissions } from "../rbac/hasAccess";
import { validateRequiredFields } from "../../../utils/commonComponent/errorAlert";
import { decryptPassword, encryptPassword } from "./crypto";


// Filter out disabled modules
const EnabledModules = moduleList.filter(
  (module) => !DISABLED_MODULE.includes(module.name)
);

// File Upload Field
const FileUploadComponent = ({
  fileUploadResponse,
  setFileUploadResponse,
  label,
}) => {
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    // Allowed image types
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "image/webp"];

    if (!allowedTypes.includes(file.type)) {
      toast.error("Only image files (JPG, PNG, WEBP) are allowed!");
      return;
    }
    const data = new FormData();
    data.append("fileToUpload", file);

    axiosWithToken
      .post(BASE_URL + `fileUpload/fileUpload.php`, data)
      .then((res) => {
        if (res.status === 200) {
          setFileUploadResponse(res.data);
          toast.success("File Uploaded Successfully!");
        }
      })
      .catch((err) => {
        if (err.message !== "request_aborted")
          toast.error("Something Went Wrong!");
      });
  };

  return (
    <div className="w-full mx-5">
      <UploadButton
        label={label}
        onChange={handleFileUpload}
        fileUrl={fileUploadResponse}
      />
    </div>
  );
};

export default function EditUserMaster(props) {
  const params = useParams();
  const permissions = getPermissions(props.moduleId);
  const [inputStates, setInputStates] = useState({
    userName: "",
    password: "",
    passVal: [0, 0, 0, 0, 0],
    type: null,
    roles: [],
    selectedModules: [],
  });

  const [executing, setExecuting] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [modules, setModules] = useState(EnabledModules);
  const [fileUploadResponse, setFileUploadResponse] = useState(null);
  const [employeeList, setEmployeeList] = useState([]);
  const [rollList, setrollList] = useState([]);

  const updateInputObject = (key, value) =>
    setInputStates((prev) => ({ ...prev, [key]: value }));

  const refresh = () => window.location.reload(false);

  const onSubmit = async () => {
    // show alert
    const fieldsToValidate = [
      { value: inputStates.employeeId, label: "Employee selection Required" },
      { value: inputStates.userName, label: "UserName Required" },
      { value: inputStates.type, label: "Type selection required" },
      { value: inputStates.roles.length, label: "Please select at least one role" },
    ];
    if (!validateRequiredFields(fieldsToValidate)) {
      return;
    }

    if (inputStates.type != 1) {
      if (!inputStates.selectedModules?.length)
        return toast.error("Please select at least one module!");
    }
    setExecuting(true);


    // 🔐 Encrypt password before passing to object
    const encryptedPassword = await encryptPassword(inputStates.password);
    

    const jsonData = {
      userName: inputStates.userName,
      password: encryptedPassword,
      modules: inputStates.selectedModules.map((m) => m.id).join(",") || null,
      roles: inputStates.roles?.length > 0
        ? inputStates.roles.map((d) => d.id).join(",")
        : null,
      type: inputStates.type || null,
      usersign: fileUploadResponse || null,
      employeeId: inputStates?.employeeId || null,
      status: 1,
    };

    const apiCall =
      params.id === "0"
        ? axiosWithToken.post(BASE_URL + `users`, jsonData)
        : axiosWithToken.patch(BASE_URL + `users/${params.id}`, jsonData);

    apiCall
      .then(() => {
        toast.success(
          params.id === "0"
            ? "User Created Successfully!"
            : "Updated Successfully!"
        );
        refresh();
      })
      .catch(() => toast.error("Something Went Wrong!"));
  };

  const fetchUserDetails = async () => {
    const { data } = await axiosWithToken.get(BASE_URL + `users/${params.id}`);
    const user = data[0];
   const decryptedPassword = await decryptPassword(user.password);
    const password = decryptedPassword || "";

    const passVal = [
      /[a-z]/.test(password) ? 1 : 0,
      /[A-Z]/.test(password) ? 1 : 0,
      /[0-9]/.test(password) ? 1 : 0,
      password.length >= 8 ? 1 : 0,
      password.length <= 32 ? 1 : 0,
    ];

    updateInputObject("userName", user.userName);
    updateInputObject("password", password);
    updateInputObject("passVal", passVal);
    setFileUploadResponse(user?.usersign);

    // Handle single type selection
    updateInputObject(
      "type",
      user.type ? userTypes.find((u) => u.id == user.type)?.id : null
    );

    updateInputObject("employeeId", user.employeeId);

    updateInputObject(
      "roles",
      user.roles
        ? user.roles
          .split(",")
          .map((id) => userRoles.find((d) => d.id == id))
          .filter(Boolean)
        : []
    );

    updateInputObject(
      "selectedModules",
      user.modules
        ? user.modules
          .split(",")
          .map((id) => rollList.find((mod) => mod.id == parseInt(id.trim())))
        : []
    );
  };

  const fetchEmployeesData = async () => {
    try {
      const Employees = await fetchQuery(
        `select id,employeesCode,name from employees where status = 1`,
      );
      setEmployeeList(Employees);
    } catch (error) {
      console.error("Error fetching employee data:", error);
    }
  };

  useEffect(() => {
    fetchEmployeesData();
    fetchRBACData(false, setrollList);
  }, []);

  useEffect(() => {
    if (params.id != 0) { fetchUserDetails() };
  }, [params, rollList]);

  const handlePasswordChange = (val) => {
    const passVal = [
      /[a-z]/.test(val) ? 1 : 0,
      /[A-Z]/.test(val) ? 1 : 0,
      /[0-9]/.test(val) ? 1 : 0,
      val.length >= 8 ? 1 : 0,
      val.length <= 32 ? 1 : 0,
    ];
    updateInputObject("password", val);
    updateInputObject("passVal", passVal);
  };

  return (
    <div>
      <Text variant="h3" bold className="text-blue-600">
        {params.id == 0 ? "Create New User" : `Update ${inputStates?.userName}`}
      </Text>
      <br />
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <div className="w-full">
          <Autocomplete
            sx={{ width: "100%" }}
            size="small"
            options={employeeList}
            getOptionLabel={(option) =>
              `${option.name} (${option.employeesCode})`
            }
            value={
              employeeList.find((emp) => emp.id === inputStates.employeeId) ||
              null
            }
            onChange={(e, val) =>
              updateInputObject("employeeId", val ? val.id : null)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Employee *"
                placeholder="Employee *"
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
        </div>
        
        <div className="w-full">
          <TextFields
            label="Username *"
            value={inputStates.userName}
            onChange={(e) => updateInputObject("userName", e.target.value)}
          />
        </div>

        <div className="w-full">
          <TextFields
            className="relative"
            label="Password *"
            value={inputStates.password}
            onChange={(e) => handlePasswordChange(e.target.value)}
          />
          <InfoIcon
            style={{ color: "gray", position: "absolute", height: "20px" }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          />
          {isHovered && (
            <div className="password-validation-info">
              {[
                ["A lowercase letter", inputStates.passVal[0]],
                ["A capital letter", inputStates.passVal[1]],
                ["A number", inputStates.passVal[2]],
                ["Minimum 8 characters", inputStates.passVal[3]],
                ["Maximum 32 characters", inputStates.passVal[4]],
              ].map(([text, valid], i) => (
                <p
                  key={i}
                  className={valid ? "text-green-600" : "text-red-600"}
                >
                  {text}
                </p>
              ))}
            </div>
          )}
        </div>
        <FileUploadComponent
          fileUploadResponse={fileUploadResponse}
          setFileUploadResponse={setFileUploadResponse}
          label="Upload Image"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-8">
        {/* Single selection for User Types */}
        <div className="w-full">
          <AutoCompletes
            label="User Types *"
            options={userTypes}
            value={inputStates.type}
            onChange={(val) => updateInputObject("type", val)}
            isWidth
            valueKey="id"
            labelKey="label"
            disabled={params.id !== "0"}
          />
        </div>
        <div className="w-full">
          <Autocomplete
            multiple
            sx={{ width: "100%" }}
            size="small"
            options={userRoles}
            value={inputStates?.roles}
            disableCloseOnSelect
            getOptionLabel={(option) => option?.label || ""}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Roles"
                placeholder="Select multiple Roles"
              />
            )}
            onChange={(e, val) => updateInputObject("roles", val)}
          />
        </div>

        {(() => {
          const allowedRoles = ["l1", "hod", "h1", "h2", "l2"];

          const filteredRollList =
            // inputStates?.type == 1 || inputStates?.type == 2
            //   ? rollList.filter((r) =>
            //     allowedRoles.some((role) =>
            //       r.roleName?.toLowerCase().includes(role.toLowerCase()),
            //     ),
            //   )
            //   :
               rollList;

          return (
            inputStates.type != 1 && (
              <div className="w-full md:col-span-2">
                <Autocomplete
                  multiple
                  size="small"
                  options={[
                    { id: 0, roleName: "Select All" },
                    ...[...filteredRollList].sort((a, b) =>
                      (a.roleName || "").localeCompare(b.roleName || ""),
                    ),
                  ]}
                  value={inputStates.selectedModules}
                  disableCloseOnSelect
                  getOptionLabel={(option) => option?.roleName || ""}
                  renderOption={(props, option, { selected }) => (
                    <li
                      {...props}
                      // style={{ paddingLeft: option.id > 50 ? "1rem" : "0" }}
                    >
                      <Checkbox
                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      {option.roleName}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select modules (optional) *"
                      placeholder="Select multiple modules"
                    />
                  )}
                  limitTags={2}
                  onChange={(e, val) => {
                    if (val.some((v) => v.id == 0)) {
                      const allSelected =
                        inputStates.selectedModules.length ===
                        filteredRollList.length;
                      updateInputObject(
                        "selectedModules",
                        allSelected ? [] : filteredRollList,
                      );
                    } else {
                      updateInputObject("selectedModules", val);
                    }
                  }}
                />
              </div>
            )
          );
        })()}
      </div>

      <div className="flex gap-10 mt-5 justify-end">
        <CancelButton
          label="Cancel"
          outline
          onClick={() => {
            if (window.confirm("Are you sure you want to cancel?")) {
              window.history.back();
            }
          }}
        />
        {(permissions.save || permissions.create || permissions.allAccess) && (
          <SaveAndUpdate
            label={`${params.id !== "0" ? "Update" : "Save"} ${inputStates.userName
              }`}
            onClick={onSubmit}
            disabled={
              executing ||
              !inputStates.userName ||
              !inputStates.password ||
              inputStates.passVal.reduce((a, b) => a + b, 0) < 5 ||
              (params.id !== "0" && !inputStates.type)
            }
          />
        )}
      </div>
    </div>
  );
}
